import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, InlineNote, Table, type TableColumn } from '../../../components/shared';
import { layerConfirm } from '../../../components/LayerPopup/LayerPopup';
import {
  listBoardTrash, purgeBoardPost, restoreBoardPost,
  type BoardDto, type BoardTrashPost,
} from '../../../api/boards';
import { tBoards } from '../../../lib/boards-i18n';
import { BoardPrefixPill } from '../BoardMarks';
import { formatListDate } from './board-list-model';

/**
 * 휴지통 — 시안 v1 `list-trash` (U2). 데이터 경로는 현행 `BoardTrash` 와 같다.
 *
 * ★ 영구 삭제 확인은 `layerConfirm`(정본 F1) — 현행의 `window.confirm` 을 쓰지 않는다.
 * ★ 남은 기간이 3일 이하면 강조한다(시안 `.left3`) — 색만이 아니라 숫자가 함께 있다.
 * ★ 서버가 이 라우트를 모르면(404/405) 이유를 말하고 부모가 진입점을 감춘다(배포 순서 안전장치 — 현행과 같음).
 */

type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };
const errMsg = (e: unknown) => (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown';

export interface BoardTrashCardProps {
  board: BoardDto;
  onRestored: () => void;
  onUnsupported: () => void;
}

export function BoardTrashCard({ board, onRestored, onUnsupported }: BoardTrashCardProps) {
  const [posts, setPosts] = useState<BoardTrashPost[]>([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const unsupportedRef = useRef(onUnsupported);
  unsupportedRef.current = onUnsupported;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await listBoardTrash(board.id);
      setPosts(r.posts);
      setRetentionDays(r.retentionDays);
    } catch (e) {
      const status = (e as ApiError)?.response?.status;
      if (status === 404 || status === 405) {
        setError(tBoards('boards.trash.unsupported'));
        unsupportedRef.current();
        return;
      }
      setError(tBoards('boards.trash.err_load', { msg: errMsg(e) }));
    } finally {
      setLoading(false);
    }
  }, [board.id]);

  useEffect(() => { void load(); }, [load]);

  async function restore(p: BoardTrashPost) {
    setBusy(p.id);
    setError(null);
    try {
      const { restoredAttachments } = await restoreBoardPost(p.id);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      setNotice(restoredAttachments > 0
        ? tBoards('boards.trash.restored_files', { n: restoredAttachments })
        : tBoards('boards.trash.restored'));
      onRestored();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  async function purge(p: BoardTrashPost) {
    const ok = await layerConfirm(tBoards('boards.trash.confirm_purge', { title: p.title }), { danger: true });
    if (!ok) return;
    setBusy(p.id);
    setError(null);
    try {
      const { filesKept } = await purgeBoardPost(p.id);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
      setNotice(filesKept > 0 ? tBoards('boards.trash.kept_files', { n: filesKept }) : null);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  }

  const columns: TableColumn<BoardTrashPost>[] = [
    { key: 'prefix', header: tBoards('boards.col.prefix'), width: '104px', render: (p) => <BoardPrefixPill label={p.prefix?.label} /> },
    { key: 'title', header: tBoards('boards.col.title'), render: (p) => <b className="bv1-trash__title" title={p.title}>{p.title}</b> },
    { key: 'author', header: tBoards('boards.col.author'), width: '120px', render: (p) => p.author.name },
    { key: 'deleted', header: tBoards('boards.trash.col_deleted'), width: '96px', render: (p) => <span className="bv1-num">{formatListDate(p.deletedAt)}</span> },
    {
      key: 'left',
      header: tBoards('boards.trash.col_left'),
      width: '96px',
      render: (p) => (
        <span className={`bv1-num${p.daysLeft <= 3 ? ' bv1-trash__soon' : ''}`}>
          {tBoards('boards.trash.days_left', { n: p.daysLeft })}
        </span>
      ),
    },
    {
      key: 'actions',
      header: tBoards('boards.col.actions'),
      align: 'right',
      width: '200px',
      render: (p) => (
        <span className="bv1-trash__acts">
          <Button variant="secondary" size="compact" disabled={busy === p.id} onClick={() => void restore(p)}>
            {tBoards('boards.trash.restore')}
          </Button>
          <Button variant="quiet-danger" size="compact" disabled={busy === p.id} onClick={() => void purge(p)}>
            {tBoards('boards.trash.purge')}
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="bv1-trash">
      <p className="bv1-card__desc">{tBoards('boards.v1.trash.desc', { days: retentionDays })}</p>
      {notice && <InlineNote tone="success">{notice}</InlineNote>}
      {error && <InlineNote tone="danger">{error}</InlineNote>}
      <Table
        caption={tBoards('boards.trash.of_board', { name: board.name })}
        columns={columns}
        rows={posts}
        rowKey={(p) => p.id}
        loading={loading}
        empty={tBoards('boards.trash.empty')}
        wrapClassName="bv1-tablewrap"
      />
    </div>
  );
}
